import React from "react";

/** Design-system primitives — SaaSLaunchingLab.
 *  Rules from /app/design_guidelines.json baked in:
 *   - Deep Navy grounding, Signal Blue primary, Mist White panels.
 *   - Cyan / Green are icon/accent only, never body text on white.
 *   - Signature gradient reserved for hero accents and thin dividers.
 *   - Montserrat headings, Inter body.
 */

const cn = (...xs) => xs.filter(Boolean).join(" ");

// -------- Logo ---------------------------------------------------------------
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_e4566ece-326c-42ef-9670-da49472420bb/artifacts/oj9anl6w_SaaSLaunchingLab_logo.png";
const LOGO_ALT = "https://customer-assets.emergentagent.com/job_e4566ece-326c-42ef-9670-da49472420bb/artifacts/9mcpidn4_SaaSLaunchingLab%20logo_negative_transp.png";

export function Logo({ variant = "light", className = "", width, height, testId, style }) {
  const src = variant === "dark" ? LOGO_ALT : LOGO_LIGHT;
  const styleObj = { ...(style || {}) };
  if (height != null) styleObj.height = height;
  if (width != null && height == null) styleObj.width = width;
  if (height != null && width == null) styleObj.width = "auto";
  return (
    <img
      data-testid={testId}
      src={src}
      alt="SaaSLaunchingLab"
      style={styleObj}
      className={cn("select-none object-contain", className)}
      draggable={false}
    />
  );
}

// -------- Gradient bar (hero/section accent only) ----------------------------
export function GradientBar({ className = "" }) {
  return (
    <div
      aria-hidden
      className={cn("h-1.5 w-full", className)}
      style={{
        background:
          "linear-gradient(90deg,#0B1F3A 0%,#2D7FF9 33%,#20C7E8 66%,#7ED957 100%)",
      }}
    />
  );
}

// -------- Button -------------------------------------------------------------
const BTN_BASE =
  "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2D7FF9] focus:ring-offset-2";
const BTN_VARIANTS = {
  primary: "bg-[#2D7FF9] text-white hover:bg-[#1A64D4] px-6 py-3",
  secondary:
    "bg-transparent border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white px-6 py-3",
  ghost: "bg-transparent text-[#0B1F3A] hover:bg-[#E8F1FF] px-4 py-2",
  danger:
    "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 px-6 py-3",
  onNavy:
    "bg-white text-[#0B1F3A] hover:bg-[#E8F1FF] px-6 py-3",
};

export function Button({ variant = "primary", className = "", ...props }) {
  return (
    <button className={cn(BTN_BASE, BTN_VARIANTS[variant], className)} {...props} />
  );
}

// -------- Card ---------------------------------------------------------------
export function Card({ className = "", children, ...rest }) {
  return (
    <div
      {...rest}
      className={cn(
        "bg-white border border-[#0B1F3A]/10 rounded-2xl p-6 md:p-8 shadow-[0_4px_24px_rgba(11,31,58,0.04)]",
        className
      )}
    >
      {children}
    </div>
  );
}

// -------- Field --------------------------------------------------------------
export function Field({ label, hint, error, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-semibold text-[#0B1F3A]">
          {label}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs text-red-600 font-medium">{error}</p>
      ) : hint ? (
        <p className="text-xs text-[#0B1F3A]/60">{hint}</p>
      ) : null}
    </div>
  );
}

const INPUT_CLASS =
  "w-full border border-[#0B1F3A]/20 rounded-lg px-4 py-3 bg-white text-[#0B1F3A] placeholder-[#0B1F3A]/40 focus:ring-2 focus:ring-[#2D7FF9] focus:border-transparent outline-none transition-all";

export function Input({ className = "", ...props }) {
  return <input {...props} className={cn(INPUT_CLASS, className)} />;
}

export function Textarea({ className = "", ...props }) {
  return <textarea {...props} className={cn(INPUT_CLASS, "min-h-24", className)} />;
}

// -------- Badge --------------------------------------------------------------
const BADGE_VARIANTS = {
  founder: "bg-[#0B1F3A] text-white",
  community: "bg-[#E8F1FF] text-[#2D7FF9]",
  ambassador:
    "bg-[#0B1F3A] text-white ring-1 ring-inset ring-[#7ED957]/70",
  admin: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  active: "bg-[#E8F1FF] text-[#0B1F3A]",
  neutral: "bg-[#E8F1FF] text-[#0B1F3A]",
  ai: "bg-[#E8F1FF] text-[#0B1F3A]",
  premium:
    "text-white ring-1 ring-inset ring-white/25 shadow-sm bg-gradient-to-r from-[#0B1F3A] via-[#2D7FF9] to-[#20C7E8]",
};

export function Badge({ variant = "neutral", className = "", children, ...rest }) {
  return (
    <span
      {...rest}
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider",
        BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral,
        className
      )}
    >
      {children}
    </span>
  );
}

/** Signature Premium pill — small, on-brand, safe to drop next to any user name. */
export function PremiumBadge({ className = "", label = "Premium", withStar = true, ...rest }) {
  return (
    <Badge variant="premium" className={cn("px-2 py-0.5", className)} data-testid="premium-badge" {...rest}>
      {withStar && (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      )}
      {label}
    </Badge>
  );
}

// -------- Avatar -------------------------------------------------------------
export function Avatar({ name, className = "" }) {
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className={cn(
        "w-10 h-10 rounded-full bg-[#E8F1FF] border border-[#0B1F3A]/10 flex items-center justify-center text-[#2D7FF9] font-bold text-sm",
        className
      )}
    >
      {initials}
    </div>
  );
}

// -------- Empty / Loading / Error states -------------------------------------
export function EmptyState({ title, description, action, icon = null }) {
  return (
    <div className="flex flex-col items-start gap-3 py-14 px-6 border border-dashed border-[#0B1F3A]/15 rounded-2xl bg-white">
      {icon}
      <h3 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">{title}</h3>
      {description && <p className="text-sm text-[#0B1F3A]/70 max-w-md">{description}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = "Loading..." }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#0B1F3A]/70 py-6">
      <div className="w-4 h-4 rounded-full border-2 border-[#2D7FF9] border-t-transparent animate-spin" />
      {label}
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", detail, action }) {
  return (
    <div className="p-6 border border-red-200 bg-red-50 rounded-2xl">
      <h3 className="font-[Montserrat] font-bold text-lg text-red-800">{title}</h3>
      {detail && <p className="text-sm text-red-700 mt-1">{detail}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
